import type { ViewComponent, ViewDefinition, ViewGrid } from './viewTypes'

export interface RecordWorkspaceTab {
    key: string
    label: string
    owner: ViewComponent
    components: ViewComponent[]
}

/** Derive page chrome from authored structure; never rewrite the saved view or its bindings. */
export function recordWorkspaceLayout(view: ViewDefinition, grid: ViewGrid = view.grid) {
    if (view.kind === 'dashboard') return undefined
    const components = grid.components ?? []
    const top = components.filter(c => !c.tabSlot)
    const headers = top.filter(c => c.type === 'object-header')
    const header = headers[0]
    // Multi-object/custom canvases and partial-width headers keep their authored layout.
    if (headers.length !== 1 || !header || header.col !== 1 || header.colSpan !== 24) return undefined
    const tabs: RecordWorkspaceTab[] = []
    const promoted = new Set<ViewComponent>()
    for (const owner of top) {
        if (owner.type !== 'tab-container' || owner.tabPlacement === 'inline' || !owner.tabs?.length) continue
        const slots = owner.tabs.map(tab => ({
            ...tab, components: components.filter(c => c.tabSlot === tab.slot),
        }))
        // Existing generated related tabs gain the page layout on upgrade. Authored field tabs,
        // nested containers and document line grids keep their own layout unless explicitly opted in.
        if (owner.tabPlacement !== 'page' && !slots.every(tab =>
            tab.components.length > 0 && tab.components.every(c => c.type === 'related-entity-list'))) continue
        if (new Set(slots.map(tab => tab.slot)).size !== slots.length) continue
        if (slots.some(tab => top.some(other => other !== owner && other.tabs?.some(t => t.slot === tab.slot)))) continue
        promoted.add(owner)
        slots.forEach(tab => tabs.push({ key: `related:${tab.slot}`, label: tab.label, owner, components: tab.components }))
    }
    return { header, general: top.filter(c => c !== header && !promoted.has(c)), tabs }
}

const fieldTypes = new Set([
    'text-input', 'number-input', 'date-picker', 'select', 'checkbox', 'lookup-field', 'field-section',
    'user-picker', 'phone-number', 'rich-text', 'quantity-input', 'status-badge',
])

/** Only ordinary field bands form paired panels. Maps, line grids and specialist content stay full width. */
export function recordFieldGroups(components: ViewComponent[]) {
    const groups: Array<{ key: string; heading?: ViewComponent; components: ViewComponent[]; fullWidth: boolean }> = []
    let current: (typeof groups)[number] | undefined
    for (const component of components) {
        if (component.type === 'section-header') {
            current = { key: `${component.groupKey ?? 'section'}-${groups.length}`, heading: component, components: [], fullWidth: false }
            groups.push(current)
        } else {
            if (!current) {
                current = { key: `content-${groups.length}`, components: [], fullWidth: true }
                groups.push(current)
            }
            current.components.push(component)
            // Keep specialist content with its heading and authored grid; never squeeze line grids,
            // composite editors or inline tabs into a paired field panel.
            if (!fieldTypes.has(component.type)) current.fullWidth = true
        }
    }
    const populated = groups.filter(group => group.components.length > 0)
    let paired = 0
    populated.forEach((group, index) => {
        if (group.fullWidth) { paired = 0; return }
        paired++
        // Complete an odd field band before the next full-width panel instead of leaving a hole.
        if (paired % 2 === 1 && (!populated[index + 1] || populated[index + 1]!.fullWidth)) group.fullWidth = true
    })
    return populated
}
